import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import clsx from 'clsx';
import { Icon } from '../../../../layout/Icon.jsx';

const schema = Joi.object({
  name: Joi.string().trim().min(2).required().messages({
    'string.empty': 'الاسم مطلوب',
    'string.min': 'الاسم قصير جداً',
    'any.required': 'الاسم مطلوب'
  }),
  pricePerHour: Joi.number().min(0).required().messages({
    'number.base': 'سعر الساعة مطلوب',
    'any.required': 'سعر الساعة مطلوب'
  }),
  minimumCharge: Joi.number().min(0).default(0),
  graceMinutes: Joi.number().integer().min(0).max(60).default(0),
  roundingType: Joi.string().valid('exact', 'ceil_hour', 'ceil_half_hour').default('ceil_hour'),
  applicableDays: Joi.array().items(Joi.number().integer().min(0).max(6)).default([0, 1, 2, 3, 4, 5, 6]),
  startTime: Joi.string().allow('').optional(),
  endTime: Joi.string().allow('').optional(),
  holidayPrice: Joi.number().min(0).allow(null, '').optional(),
  isDefault: Joi.boolean().default(false),
  isActive: Joi.boolean().default(true)
}).unknown(true);

const ROUNDING = [
  { key: 'exact', label: 'دقيق' },
  { key: 'ceil_half_hour', label: 'نصف ساعة' },
  { key: 'ceil_hour', label: 'ساعة كاملة' }
];

const DAY_LABELS = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

function defaultsFrom(rule) {
  return {
    name: rule?.name || '',
    pricePerHour: rule?.pricePerHour ?? 10000,
    minimumCharge: rule?.minimumCharge ?? 0,
    graceMinutes: rule?.graceMinutes ?? 0,
    roundingType: rule?.roundingType || 'ceil_hour',
    applicableDays: Array.isArray(rule?.applicableDays) ? rule.applicableDays : [0, 1, 2, 3, 4, 5, 6],
    startTime: rule?.startTime || '',
    endTime: rule?.endTime || '',
    holidayPrice: rule?.holidayPrice ?? '',
    isDefault: !!rule?.isDefault,
    isActive: rule?.isActive !== false
  };
}

export function RuleForm({ open, rule, currentDefault, onClose, onSubmit, submitting }) {
  const isEdit = !!rule;
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors }
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: defaultsFrom(rule)
  });

  useEffect(() => {
    if (open) reset(defaultsFrom(rule));
  }, [open, rule, reset]);

  const watchIsDefault = watch('isDefault');

  if (!open) return null;

  const submit = (values) => {
    const body = {
      ...values,
      holidayPrice:
        values.holidayPrice === '' || values.holidayPrice == null ? null : Number(values.holidayPrice),
      startTime: values.startTime || null,
      endTime: values.endTime || null
    };
    onSubmit?.(body);
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.45)' }}
    >
      <form
        className="card w-[640px] max-w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit(submit)}
        noValidate
      >
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">
            {isEdit ? 'تعديل قاعدة تسعير' : 'قاعدة تسعير جديدة'}
          </h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="card-b space-y-3">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الاسم *</label>
            <input
              className={clsx('input', errors.name && 'border-red')}
              placeholder="مثلاً: التسعير الأساسي"
              {...register('name')}
            />
            {errors.name ? (
              <div className="text-red text-[11.5px] mt-1 font-semibold">{errors.name.message}</div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">سعر الساعة (د.ع) *</label>
              <input
                className={clsx('input', errors.pricePerHour && 'border-red')}
                type="number"
                min="0"
                {...register('pricePerHour', { valueAsNumber: true })}
              />
              {errors.pricePerHour ? (
                <div className="text-red text-[11.5px] mt-1 font-semibold">
                  {errors.pricePerHour.message}
                </div>
              ) : null}
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">أقل مبلغ (د.ع)</label>
              <input
                className="input"
                type="number"
                min="0"
                {...register('minimumCharge', { valueAsNumber: true })}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">دقائق السماح (0-60)</label>
              <input
                className="input"
                type="number"
                min="0"
                max="60"
                {...register('graceMinutes', { valueAsNumber: true })}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">سعر العطلة (د.ع)</label>
              <input
                className="input"
                type="number"
                min="0"
                placeholder="اختياري"
                {...register('holidayPrice')}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">طريقة التقريب</label>
            <Controller
              control={control}
              name="roundingType"
              render={({ field }) => (
                <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px] w-full">
                  {ROUNDING.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => field.onChange(r.key)}
                      className={clsx(
                        'flex-1 px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                        field.value === r.key
                          ? 'bg-orange text-white shadow-orange-glow'
                          : 'text-ink-2 hover:bg-white'
                      )}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">أيام التطبيق</label>
            <Controller
              control={control}
              name="applicableDays"
              render={({ field }) => (
                <div className="flex flex-wrap gap-1.5">
                  {DAY_LABELS.map((label, idx) => {
                    const active = (field.value || []).includes(idx);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          const set = new Set(field.value || []);
                          if (set.has(idx)) set.delete(idx);
                          else set.add(idx);
                          field.onChange(Array.from(set).sort((a, b) => a - b));
                        }}
                        className={clsx(
                          'px-3 py-1.5 rounded-[10px] text-[12px] font-semibold border transition-colors',
                          active
                            ? 'bg-orange text-white border-orange'
                            : 'bg-white text-ink-2 border-line hover:bg-paper-2'
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">من ساعة</label>
              <input className="input" type="time" {...register('startTime')} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">إلى ساعة</label>
              <input className="input" type="time" {...register('endTime')} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <label className="flex items-center gap-2 cursor-pointer text-[13px] text-ink-2">
              <input type="checkbox" className="accent-orange" {...register('isDefault')} />
              افتراضية
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-[13px] text-ink-2">
              <input type="checkbox" className="accent-orange" {...register('isActive')} />
              فعّالة
            </label>
          </div>

          {watchIsDefault && currentDefault && (currentDefault._id || currentDefault.id) !== (rule?._id || rule?.id) ? (
            <div className="p-2.5 rounded-[10px] bg-paper-2 text-ink-2 text-[12px] flex items-center gap-2">
              <Icon name="clock" size={14} />
              <div>سيتم إلغاء افتراضية القاعدة الحالية: <b>{currentDefault.name}</b></div>
            </div>
          ) : null}
        </div>

        <div className="card-h border-b-0 border-t flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            إلغاء
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'جاري الحفظ...' : isEdit ? 'حفظ التعديلات' : 'إنشاء القاعدة'}
          </button>
        </div>
      </form>
    </div>
  );
}
